// =====================================================
// CATALOG TREE ITEM - Componente individual del árbol
// =====================================================

import { ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import type { CatalogItemType } from '../../types/catalog';

interface CatalogTreeItemProps {
  id: string;
  code: string;
  name: string;
  type: CatalogItemType;
  isLinked: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  hasChildren: boolean;
  linkedCount?: number;
  totalCount?: number;
  depth: number;
  onToggleExpand: () => void;
  onToggleLink: (id: string, type: CatalogItemType, currentlyLinked: boolean) => void;
  children?: React.ReactNode;
  // Props para búsqueda
  isHighlighted?: boolean;
  searchQuery?: string;
}

// Colores según tipo
const typeColors: Record<CatalogItemType, { bg: string; text: string; border: string }> = {
  specialty: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  subspecialty: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },
  treatmentGroup: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  medicalAct: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
};

// Función para normalizar texto
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

// Componente para resaltar texto que coincide con la búsqueda
function HighlightedText({ text, query }: { text: string; query?: string }) {
  if (!query || !query.trim()) {
    return <>{text}</>;
  }

  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);
  const index = normalizedText.indexOf(normalizedQuery);

  if (index === -1) {
    return <>{text}</>;
  }

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <mark className="bg-yellow-200 text-inherit rounded px-0.5">{match}</mark>
      {after}
    </>
  );
}

export function CatalogTreeItem({
  id,
  code,
  name,
  type,
  isLinked,
  isExpanded,
  isLoading,
  hasChildren,
  linkedCount,
  totalCount,
  depth,
  onToggleExpand,
  onToggleLink,
  children,
  isHighlighted = false,
  searchQuery,
}: CatalogTreeItemProps) {
  const colors = typeColors[type];
  const paddingLeft = depth * 24 + 12;

  const handleCheckboxChange = () => {
    if (!isLoading) {
      onToggleLink(id, type, isLinked);
    }
  };

  return (
    <div className="select-none">
      {/* Item row */}
      <div
        className={`
          flex items-center gap-2 py-2 px-3 rounded-lg
          hover:bg-gray-50 transition-colors group
          ${isLinked ? 'bg-white' : 'bg-gray-50/50'}
          ${isHighlighted ? 'ring-2 ring-yellow-300 ring-inset bg-yellow-50/50' : ''}
        `}
        style={{ paddingLeft }}
      >
        {/* Expand/collapse button */}
        {hasChildren ? (
          <button
            onClick={onToggleExpand}
            className="p-0.5 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}

        {/* Checkbox */}
        <div className="flex-shrink-0">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-primary animate-spin" />
          ) : (
            <input
              type="checkbox"
              checked={isLinked}
              onChange={handleCheckboxChange}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer"
            />
          )}
        </div>

        {/* Type badge */}
        <span
          className={`
            px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex-shrink-0
            ${colors.bg} ${colors.text}
          `}
        >
          <HighlightedText text={code} query={searchQuery} />
        </span>

        {/* Name */}
        <span
          className={`
            text-sm font-medium truncate flex-1
            ${isLinked ? 'text-gray-900' : 'text-gray-500'}
          `}
        >
          <HighlightedText text={name} query={searchQuery} />
        </span>

        {/* Counters (para items con hijos) */}
        {linkedCount !== undefined && totalCount !== undefined && totalCount > 0 && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            {linkedCount}/{totalCount}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="relative">
          {/* Línea vertical de conexión */}
          <div
            className="absolute top-0 bottom-0 w-px bg-gray-200"
            style={{ left: paddingLeft + 10 }}
          />
          {children}
        </div>
      )}
    </div>
  );
}

export default CatalogTreeItem;
